import React, { useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import { Link, useSearchParams } from "react-router-dom";
import { DateTime } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import { pettyWorkStatusMap } from "features/PettyWorks/constants/petty-work.constants";
import OverviewTab from "./OverviewTab";
import LineItemsTab from "./LineItemsTab";
import MaterialRequestsTab from "./MaterialRequestsTab";
import PettyWorkLifecycleButtons from "features/PettyWorks/components/PettyWorkLifecycleButtons";
import { invalidateReadyForBillingPettyWorkCount } from "features/PettyWorks/pettyWorkReadyForBillingCountInvalidate";
import { usePettyWorkOverview } from "./PettyWorkOverviewContext";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { canEditPettyWorkStatus } from "features/PettyWorks/utils/pettyWorkActionPolicy";
import {
  PettyWorkOverviewTabParam,
  isValidPettyWorkOverviewTabParam,
  pettyWorkOverviewTabIdFromSearchParam,
  searchParamForPettyWorkOverviewTabId,
} from "./overviewTabSearchParams";

const Section = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pettyWork, refetch } = usePettyWorkOverview();
  const { can } = useAccess();
  const canEditPettyWork = can(Permissions.PETTY_WORK.UPDATE);

  const tabQuery = searchParams.get("tab");

  useEffect(() => {
    if (tabQuery && !isValidPettyWorkOverviewTabParam(tabQuery)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("tab");
          return next;
        },
        { replace: true },
      );
    }
  }, [tabQuery, setSearchParams]);

  if (!pettyWork) {
    return (
      <Row>
        <Col lg={12}>
          <p className="text-muted mb-0">
            This petty work could not be loaded.
          </p>
        </Col>
      </Row>
    );
  }

  const displayTitle = pettyWork.title || pettyWork.code || "Petty work";
  const codeLine =
    pettyWork.code && pettyWork.title && pettyWork.code !== pettyWork.title
      ? pettyWork.code
      : null;

  const activeTab = pettyWorkOverviewTabIdFromSearchParam(tabQuery);

  const setTabInUrl = (tabId: string) => {
    const param = searchParamForPettyWorkOverviewTabId(tabId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!param || param === PettyWorkOverviewTabParam.OVERVIEW) {
          next.delete("tab");
        } else {
          next.set("tab", param);
        }
        return next;
      },
      { replace: true },
    );
  };

  return (
    <React.Fragment>
      <Row>
        <Col lg={12}>
          <Card className="mt-n4 mx-n4">
            <div className="bg-light-subtle">
              <CardBody className="pb-0 px-4">
                <Row className="mb-3 align-items-start">
                  <div className="col-md">
                    <div>
                      <h4 className="fw-bold">{displayTitle}</h4>
                      {codeLine ? (
                        <p className="text-muted mb-1">
                          <span className="fw-medium">{codeLine}</span>
                        </p>
                      ) : null}
                      <div className="hstack gap-3 flex-wrap">
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted">Status:</span>
                          <StatusBadge
                            value={pettyWork.status}
                            map={pettyWorkStatusMap}
                          />
                        </div>
                        {pettyWork.created_at ? (
                          <>
                            <div className="vr d-none d-sm-block" />
                            <div className="text-muted">
                              Created:{" "}
                              <span className="fw-medium">
                                <DateTime date={pettyWork.created_at} />
                              </span>
                            </div>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-auto mt-3 mt-md-0">
                    <div className="hstack gap-2 flex-wrap justify-content-md-end">
                      <PettyWorkLifecycleButtons
                        pettyWork={pettyWork}
                        onSuccess={() => {
                          void refetch();
                          invalidateReadyForBillingPettyWorkCount();
                        }}
                      />
                      {canEditPettyWork && canEditPettyWorkStatus(pettyWork.status) ? (
                        <Link
                          to={`/petty-works/edit/${pettyWork.id}`}
                          className="btn btn-light btn-sm border"
                        >
                          <i className="ri-pencil-line align-bottom me-1" />
                          Edit
                        </Link>
                      ) : null}
                    </div>
                  </div>
                </Row>

                <Nav className="nav-tabs-custom border-bottom-0" role="tablist">
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "1" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("1");
                      }}
                      href="#"
                    >
                      Overview
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "2" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("2");
                      }}
                      href="#"
                    >
                      Line items
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "3" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("3");
                      }}
                      href="#"
                    >
                      Material Requests
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardBody>
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <TabContent activeTab={activeTab} className="text-muted">
            <TabPane tabId="1">
              <OverviewTab />
            </TabPane>
            <TabPane tabId="2">
              <LineItemsTab />
            </TabPane>
            <TabPane tabId="3">
              <MaterialRequestsTab />
            </TabPane>
          </TabContent>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default Section;
